import { useState, useEffect, useCallback } from 'react'
import { r2rClient as defaultR2rClient } from 'r2r-js'
import { useAuth } from 'src/auth'
import { useOrgName } from 'src/hooks/useOrgName'

// Singleton client instance
let clientInstance: defaultR2rClient | null = null
let lastAuthState: {
  token: string | null
  refreshToken: string | null
  baseUrl: string | null
  expiresAt: number | null
} = {
  token: null,
  refreshToken: null,
  baseUrl: null,
  expiresAt: null,
}

interface R2RAuthState {
  token: string | null
  refreshToken: string | null
  baseUrl: string | null
  expiresAt: number | null
  isLoading: boolean
  error: Error | null
  hasClient: boolean
}

/**
 * Hook for accessing the R2R client
 * Initializes client only once and manages auth state
 */
const useR2R = () => {
  const { isAuthenticated, getToken } = useAuth()
  const orgSlug = useOrgName()

  const [auth, setAuth] = useState<R2RAuthState>({
    token: lastAuthState.token,
    refreshToken: lastAuthState.refreshToken,
    baseUrl: lastAuthState.baseUrl,
    expiresAt: lastAuthState.expiresAt,
    isLoading: false,
    error: null,
    hasClient: !!clientInstance,
  })

  const fetchR2RAuth = useCallback(async () => {
    if (!isAuthenticated || !orgSlug) return

    setAuth((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const token = await getToken()
      const headers = {
        orgSlug,
        Authorization: `Bearer ${token}`,
        'auth-provider': 'clerk',
      }
      const response = await fetch('/.netlify/functions/r2rAuth', {
        method: 'POST',
        headers,
      })

      if (!response.ok) {
        throw new Error('Failed to fetch R2R auth token')
      }

      const data = await response.json()

      // Initialize the client with the received token if not already initialized
      if (!clientInstance) {
        clientInstance = new defaultR2rClient(data.baseUrl)
      }

      // Always update tokens as they may have changed
      clientInstance.setTokens(data.token, data.refreshToken)

      // Update the shared auth state
      lastAuthState = {
        token: data.token,
        refreshToken: data.refreshToken,
        baseUrl: data.baseUrl,
        expiresAt: data.expiresAt,
      }

      setAuth({
        token: data.token,
        refreshToken: data.refreshToken,
        baseUrl: data.baseUrl,
        expiresAt: data.expiresAt,
        isLoading: false,
        error: null,
        hasClient: true,
      })
    } catch (error) {
      console.error('Error fetching R2R auth:', error)
      setAuth((prev) => ({
        ...prev,
        isLoading: false,
        hasClient: !!clientInstance, // Still may have a client, just failed to refresh auth
        error:
          error instanceof Error
            ? error
            : new Error('Failed to fetch R2R auth token'),
      }))
    }
  }, [isAuthenticated, orgSlug, getToken])

  // Initialize auth when authenticated and orgSlug is available
  useEffect(() => {
    if (isAuthenticated && orgSlug && !auth.token) {
      fetchR2RAuth()
    }
  }, [isAuthenticated, orgSlug, fetchR2RAuth, auth.token, auth.expiresAt])

  // TODO: Set up auto-refresh before token expires

  return {
    client: clientInstance,
    auth,
    refreshAuth: fetchR2RAuth,
  }
}

export default useR2R
